import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { CACHED_COMPANY } from '@share/constants';
import {
  CompanyResponse,
  CompanyResponses,
  CompanySearchRequest,
  HasScopeService,
} from '@openapi';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, map } from 'rxjs/operators';
import { AuthenticationService } from '../authentication/authentication.service';
import { SearchType } from './search-type.enum';
import { NGXLogger } from 'ngx-logger';
import { create } from '@share/utils/create';
import { mockCompanyData } from '@share/constants-mock-database';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private http = inject(HttpClient);
  private authService = inject(AuthenticationService);
  private logger = inject(NGXLogger);
  private apiQueryService = inject(HasScopeService);

  /**
   * Fetches the company object from the api so we have something to work with here.
   * @param fetchFromCache - optional parameter to say if this call should instead try to access local storage
   * instead of making a call to the api.  If no valid company object exists in cache then a call is made as
   * normal
   * @param overwriteEfan - optional parameter to determine if the provided efan should be used or the once in the
   *     current access token.  Note if this is set, fetch cache is ignored as it's assumed what's in the case is not
   *     what is being passed in.
   * @param searchType
   * */
  getCompanyAsSubscription$(
    fetchFromCache?: boolean,
    overwriteEfan?: string,
    searchType = SearchType.efan,
  ): Observable<CompanyResponses> {
    let url = environment.queryUrl + `/company/search?` + searchType + `=`;
    if (fetchFromCache && !overwriteEfan) {
      const company = this.authService.getFromSessionStorage(
        CACHED_COMPANY,
        create<CompanyResponse>,
      ) as CompanyResponse;
      if (company && company.efan) {
        return of({ companyArrayList: [company] });
      }
    }
    if (!!overwriteEfan) {
      url += overwriteEfan;
    } else {
      url += this.getCurrentEfan();
    }
    if (environment.envName === 'dev' && environment.mockDatabase) {
      return of({ companyArrayList: mockCompanyData() } as CompanyResponses);
    } else {
      return this.http
        .get<CompanyResponses>(url)
        .pipe(catchError(this.handleError));
    }
  }

  /**
   * Searches for practices by a specific field and value.
   * Does not create shell records in the database if search is not successful.
   *
   * The search field must be one of the values defined in the
   * `CompanySearchRequest.SearchFieldEnum` enum, and the search value is matched
   * against that field. This endpoint is only accessible to Employees.
   *
   * @param searchField - The field to search against, as defined by `CompanySearchRequest.SearchFieldEnum`.
   * @param searchValue - The value to match against the specified search field.
   * @returns An `Observable` of `CompanyResponses`. If no practices match the criteria, `companyArrayList` will be an empty array.
   */
  getCompanySearchOnly$(
    searchField: CompanySearchRequest.SearchFieldEnum,
    searchValue: string,
  ): Observable<CompanyResponses> {
    if (environment.envName === 'dev' && environment.mockDatabase) {
      return of({ companyArrayList: mockCompanyData() } as CompanyResponses);
    }

    const searchRequest: CompanySearchRequest = {
      searchField: searchField,
      searchValue: searchValue,
    };

    return this.apiQueryService
      .searchOnly(searchRequest, 'body', false)
      .pipe(catchError(this.handleError));
  }

  /**
   * Makes an EPM offices call and determines if any locations come back as determining if the practice is integrated
   * along with checking the EPM_INTEGRATION flag.
   * @param addedToCache - an optional boolean, if it set to true this will attempt to save the first integrated
   *     company to local storage with the key equating to the constant CACHED_COMPANY
   * @param fetchFromCache - optional boolean to say we want to pull this information from cache
   */
  isEpmPracticeIntegrated$(
    addedToCache?: boolean,
    fetchFromCache?: boolean,
  ): Observable<boolean> {
    return this.getCompanyAsSubscription$(fetchFromCache).pipe(
      map((company) =>
        company.companyArrayList ? company.companyArrayList : [],
      ),
      map((companies) =>
        companies.filter(
          (company) =>
            !!company.integration &&
            !!company.integration.adminOffices &&
            company.integration.epmIntegrated &&
            company.integration.adminOffices.length > 0,
        ),
      ),
      map((result) => {
        if (addedToCache && result.length > 0) {
          this.authService.addToSessionStorage(CACHED_COMPANY, result[0]);
        }
        return result;
      }),
      map((result) => result.length > 0),
    );
  }

  /**
   * using the token stored in auth service attempt to get the current efan.
   */
  getCurrentEfan(): string {
    const token = this.authService.getPeConnAccessToken();
    if (token) {
      return token.efan;
    } else {
      return '';
    }
  }

  /**
   * Returns true if the company (from getCompanyAsSubscription$) has at least one website and the first website is active.
   * This method returns an Observable<boolean>.
   */
  shouldShowManageMyWebsite(
    fetchFromCache?: boolean,
    overwriteEfan?: string,
    searchType = SearchType.efan,
  ): Observable<boolean> {
    return this.getCompanyAsSubscription$(
      fetchFromCache,
      overwriteEfan,
      searchType,
    ).pipe(
      map((companies) => {
        const company = companies.companyArrayList?.[0];
        if (
          !company ||
          !Array.isArray(company.websites) ||
          company.websites.length === 0
        ) {
          return false;
        }
        return Boolean(company.websites[0]?.active);
      }),
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    this.logger.error(
      'An error occurred in the companySearch service: ',
      error,
    );
    return error
      ? throwError(error)
      : throwError('Server Error in Search Service');
  }
}
